import { Controller } from "@hotwired/stimulus"

// Manages the narration segments editor:
// add/remove segments, edit text + pauses, save, and generate audio.
export default class extends Controller {
  static targets = [
    "editor",         // container for the segment editor
    "segmentList",    // container holding all segment items
    "statusMessage",  // small status text for save/generate feedback
    "playAllButton",  // play all button for sequential playback
  ]

  static values = {
    sceneId: String,
    saveUrl: String,
    generateUrl: String,
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  // --- Add / Remove Segments ---

  addSegment() {
    const id = crypto.randomUUID()
    const items = this.segmentListTarget.querySelectorAll("[data-segment-item]")
    const index = items.length

    // Update pause display on previously-last segment
    if (items.length > 0) {
      const lastItem = items[items.length - 1]
      const pauseRow = lastItem.querySelector("[data-pause-row]")
      if (pauseRow) pauseRow.classList.remove("hidden")
    }

    const html = this.buildSegmentHtml(id, "", 0, index, true)
    this.segmentListTarget.insertAdjacentHTML("beforeend", html)
    this.updateSegmentNumbers()

    // Focus the new textarea
    const newItem = this.segmentListTarget.querySelector(`[data-segment-id="${id}"]`)
    newItem?.querySelector("textarea")?.focus()
  }

  removeSegment(event) {
    const item = event.currentTarget.closest("[data-segment-item]")
    if (!item) return

    const items = this.segmentListTarget.querySelectorAll("[data-segment-item]")
    if (items.length <= 1) return // Keep at least one segment

    item.remove()
    this.updateSegmentNumbers()

    // Hide pause row on the new last segment
    const remaining = this.segmentListTarget.querySelectorAll("[data-segment-item]")
    if (remaining.length > 0) {
      const lastItem = remaining[remaining.length - 1]
      const pauseRow = lastItem.querySelector("[data-pause-row]")
      if (pauseRow) pauseRow.classList.add("hidden")
    }
  }

  // --- Save ---

  async saveSegments(event) {
    event.preventDefault()
    const btn = event.currentTarget
    btn.disabled = true
    const originalText = btn.textContent
    btn.textContent = "Saving..."

    const segments = this.collectSegments()

    try {
      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ scene_id: this.sceneIdValue, segments }),
      })
      const data = await response.json()
      if (data.success) {
        this.showStatus("Saved", "text-green-600")
        // Update segment IDs if any were new
        if (data.segments) {
          this.updateSegmentIds(data.segments)
        }
        if (data.narration_duration_seconds != null) {
          this.updateNarrationDuration(data.narration_duration_seconds)
        }
      } else {
        this.showStatus(data.error || "Save failed", "text-red-600")
      }
    } catch (e) {
      console.error("Save failed:", e)
      this.showStatus("Save failed", "text-red-600")
    } finally {
      btn.disabled = false
      btn.textContent = originalText
    }
  }

  // --- Generate Audio ---

  async generateSegment(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const segmentId = btn.dataset.segmentId
    btn.disabled = true
    const originalText = btn.textContent
    btn.textContent = "..."

    // Save first, then generate
    await this.saveSegmentsSilently()

    try {
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({
          scene_id: this.sceneIdValue,
          segment_id: segmentId,
        }),
      })
      const data = await response.json()
      if (data.success && data.segments) {
        this.renderSegments(data.segments)
        this.showStatus("Audio generated", "text-green-600")
        if (data.narration_duration_seconds != null) {
          this.updateNarrationDuration(data.narration_duration_seconds)
        }
      } else {
        this.showStatus(data.error || "Generation failed", "text-red-600")
      }
    } catch (e) {
      console.error("Generate failed:", e)
      this.showStatus("Generation failed", "text-red-600")
    } finally {
      btn.disabled = false
      btn.textContent = originalText
    }
  }

  async generateAll(event) {
    event.preventDefault()
    const btn = event.currentTarget
    btn.disabled = true
    const originalText = btn.textContent
    btn.textContent = "Generating..."

    await this.saveSegmentsSilently()

    try {
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ scene_id: this.sceneIdValue }),
      })
      const data = await response.json()
      if (data.success && data.segments) {
        this.renderSegments(data.segments)
        this.showStatus("All audio generated", "text-green-600")
        if (data.narration_duration_seconds != null) {
          this.updateNarrationDuration(data.narration_duration_seconds)
        }
      } else {
        this.showStatus(data.error || "Generation failed", "text-red-600")
      }
    } catch (e) {
      console.error("Generate all failed:", e)
      this.showStatus("Generation failed", "text-red-600")
    } finally {
      btn.disabled = false
      btn.textContent = originalText
    }
  }

  // --- Play All (sequential playback with pauses) ---

  async playAll() {
    if (this._playing) {
      this.stopAll()
      return
    }

    const items = this.segmentListTarget.querySelectorAll("[data-segment-item]")
    const segments = Array.from(items).map((item) => ({
      audio: item.querySelector("audio"),
      pause: parseFloat(item.querySelector("[data-pause-input]")?.value || "0"),
    })).filter((s) => s.audio)

    if (segments.length === 0) return

    this._playing = true
    this.updatePlayAllButton(true)

    for (let i = 0; i < segments.length; i++) {
      if (!this._playing) break

      const { audio, pause } = segments[i]
      audio.currentTime = 0

      await new Promise((resolve) => {
        const onEnd = () => { audio.removeEventListener("ended", onEnd); resolve() }
        audio.addEventListener("ended", onEnd)
        audio.play().catch(resolve)
      })

      if (!this._playing) break

      // Pause between segments (skip after last)
      if (i < segments.length - 1 && pause > 0) {
        await new Promise((resolve) => {
          this._pauseTimer = setTimeout(resolve, pause * 1000)
        })
      }
    }

    this._playing = false
    this.updatePlayAllButton(false)
  }

  stopAll() {
    this._playing = false
    clearTimeout(this._pauseTimer)
    // Stop any currently playing audio
    this.segmentListTarget.querySelectorAll("audio").forEach((a) => {
      a.pause()
      a.currentTime = 0
    })
    this.updatePlayAllButton(false)
  }

  updatePlayAllButton(isPlaying) {
    if (!this.hasPlayAllButtonTarget) return
    this.playAllButtonTarget.textContent = isPlaying ? "Stop" : "Play All"
  }

  // --- Pause Display ---

  updatePauseDisplay(event) {
    const input = event.currentTarget
    const label = input.closest("[data-pause-row]")?.querySelector("[data-pause-label]")
    if (label) label.textContent = `${parseFloat(input.value).toFixed(1)}s`
  }

  // --- Internal Helpers ---

  collectSegments() {
    const items = this.segmentListTarget.querySelectorAll("[data-segment-item]")
    return Array.from(items).map((item) => ({
      id: item.dataset.segmentId,
      text: item.querySelector("textarea")?.value?.trim() || "",
      pause_after_seconds: parseFloat(
        item.querySelector("[data-pause-input]")?.value || "0"
      ),
    }))
  }

  async saveSegmentsSilently() {
    const segments = this.collectSegments()
    try {
      await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ scene_id: this.sceneIdValue, segments }),
      })
    } catch { /* silent */ }
  }

  showStatus(message, colorClass) {
    if (!this.hasStatusMessageTarget) return
    const el = this.statusMessageTarget
    el.textContent = message
    el.className = `text-[10px] ${colorClass} mt-1`
    setTimeout(() => { el.textContent = "" }, 3000)
  }

  updateNarrationDuration(seconds) {
    // Update all narration duration displays in the parent modal
    const modal = this.element.closest("[data-controller~='modal']")
    if (!modal) return
    modal.querySelectorAll("[data-narration-duration]").forEach((el) => {
      el.textContent = parseFloat(seconds).toFixed(1)
    })
  }

  updateSegmentNumbers() {
    const items = this.segmentListTarget.querySelectorAll("[data-segment-item]")
    items.forEach((item, i) => {
      const num = item.querySelector("[data-segment-number]")
      if (num) num.textContent = `${i + 1}.`
    })
  }

  updateSegmentIds(serverSegments) {
    const items = this.segmentListTarget.querySelectorAll("[data-segment-item]")
    serverSegments.forEach((seg, i) => {
      if (items[i]) items[i].dataset.segmentId = seg.id || seg["id"]
    })
  }

  renderSegments(segments) {
    if (!this.hasSegmentListTarget) return
    this.segmentListTarget.innerHTML = ""

    segments.forEach((seg, i) => {
      const isLast = i === segments.length - 1
      const html = this.buildSegmentHtml(
        seg.id || seg["id"],
        seg.text || seg["text"] || "",
        seg.pause_after_seconds ?? seg["pause_after_seconds"] ?? 0.8,
        i,
        isLast,
        seg.audio_url || seg["audio_url"],
        seg.duration_seconds || seg["duration_seconds"]
      )
      this.segmentListTarget.insertAdjacentHTML("beforeend", html)
    })
  }

  buildSegmentHtml(id, text, pauseAfter, index, isLast, audioUrl, durationSeconds) {
    const pauseVal = parseFloat(pauseAfter || 0.8).toFixed(1)
    const dur = (durationSeconds || 0).toFixed(1)
    const audioSection = audioUrl
      ? `<div data-controller="audio-preview" class="flex items-center gap-1">
           <button type="button"
                   class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shrink-0"
                   data-action="audio-preview#togglePlay"
                   data-audio-preview-target="playButton">
             <svg data-audio-preview-target="playIcon" class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
             <svg data-audio-preview-target="pauseIcon" class="w-2.5 h-2.5 hidden" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>
           </button>
           <span class="text-[10px] text-gray-400">${dur}s</span>
           <audio data-audio-preview-target="audio" class="hidden"><source src="${audioUrl}"></audio>
           <input type="hidden" data-audio-preview-target="urlInput" value="${audioUrl}">
         </div>`
      : ""

    return `
      <div data-segment-item data-segment-id="${id}" class="border border-gray-200 rounded-md p-2 space-y-1.5 bg-gray-50">
        <div class="flex items-start gap-2">
          <span data-segment-number class="text-[10px] text-gray-400 mt-1.5 shrink-0">${index + 1}.</span>
          <div class="flex-1">
            <textarea rows="2"
                      class="w-full rounded border-gray-300 text-xs focus:border-blue-500 focus:ring-blue-500 py-1 px-2"
                      placeholder="Segment text">${this.escapeHtml(text)}</textarea>
          </div>
          <button type="button"
                  class="text-gray-300 hover:text-red-500 shrink-0 mt-1"
                  data-action="scripted-video--narration-segments#removeSegment"
                  title="Remove segment">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="flex items-center gap-2 ml-4">
          ${audioSection}
          <button type="button"
                  class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium text-purple-700 bg-purple-50 hover:bg-purple-100"
                  data-action="scripted-video--narration-segments#generateSegment"
                  data-segment-id="${id}">
            ${audioUrl ? "Regenerate" : "Generate"}
          </button>
        </div>
        <div data-pause-row class="ml-4 flex items-center gap-2 ${isLast ? "hidden" : ""}">
          <label class="text-[10px] text-gray-500">Pause:</label>
          <input type="range" data-pause-input min="0" max="5" step="0.1" value="${pauseVal}"
                 class="w-24 h-1 accent-indigo-500"
                 data-action="input->scripted-video--narration-segments#updatePauseDisplay">
          <span data-pause-label class="text-[10px] text-gray-500 w-6">${pauseVal}s</span>
        </div>
      </div>
    `
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
